// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { authUtils } from './utils/auth';
import { apiUpload, apiPatch, apiPost } from './utils/apiClient';
import LoginPage from './components/Auth/LoginPage';
import PDFLibrary from './components/PDFLibrary';
import FileUpload from './components/FileUpload';
import PDFViewerWithAI from './components/PDFViewerWithAI';
import SessionManager from './components/SessionManager';
import ViewModeToggle from './components/ViewModeToggle';
import SessionCanvas from './components/canvas/SessionCanvas';
import UnifiedWorkspace from './components/Workspace/UnifiedWorkspace';
import FeatureIntro from './components/FeatureIntro/FeatureIntro';
import { generatePDFFingerprint, savePDFFile, loadPDFFile } from './components/pdfUtils';
import { logger } from './utils/logger';
import './App.css';

interface PdfInfo {
  fingerprint: string;
  total_pages: number;
  file_name: string;
  file_size: number;
}

interface ImportIntent {
  pdf_url: string;
  title: string;
}

interface Session {
  id: string;
  pdf_fingerprint?: string;
  last_page?: number;
  name?: string;
  [key: string]: any;
}

type ViewType = 'library' | 'viewer' | 'upload' | 'workspace' | 'features' | 'canvas-only';

function App() {
  // 认证状态
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [authChecking, setAuthChecking] = useState<boolean>(true);

  // 应用状态
  const [currentView, setCurrentView] = useState<ViewType>('library');
  const [pdfFile, setPdfFile] = useState<string | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [pdfInfo, setPdfInfo] = useState<PdfInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // 外部导入意图（?import=1&pdf_url=...&title=...）
  const [importIntent, setImportIntent] = useState<ImportIntent | null>(null);
  const [importStatus, setImportStatus] = useState<string | null>(null); // null | 'importing' | 'done' | 'error'
  const [importError, setImportError] = useState<string>('');


  // 会话管理状态
  const [sessionMode, setSessionMode] = useState<boolean>(false); // 是否启用会话模式
  const [currentSession, setCurrentSession] = useState<Session | null>(null); // 当前选中的会话
  const [showSessionManager, setShowSessionManager] = useState<boolean>(false); // 是否显示会话管理器

  // 视图模式状态
  const [viewMode, setViewMode] = useState<'pdf' | 'canvas'>('pdf');

  // 组件挂载时检查登录状态和Canvas模式
  useEffect(() => {
    checkAuthentication();

    // Check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const isCanvasMode = urlParams.get('canvas') === 'true';
    const sessionIdParam = urlParams.get('session');

    // Handle canvas-only mode
    if (isCanvasMode && sessionIdParam) {
      setCurrentView('canvas-only');
      setSessionMode(true);
      setCurrentSession({ id: sessionIdParam });
    }

    // Handle external import (?import=1&pdf_url=...&title=...)
    if (urlParams.get('import') === '1' && urlParams.get('pdf_url')) {
      setImportIntent({
        pdf_url: urlParams.get('pdf_url')!,
        title: urlParams.get('title') || 'Untitled Paper',
      });
    }
  }, []);

  /**
   * 检查用户认证状态
   */
  const checkAuthentication = async () => {
    try {
      const response = await fetch('/api/auth/me', { credentials: 'include' });
      if (response.ok) {
        const payload = await response.json();
        authUtils.setUser(payload.user);
        setIsAuthenticated(true);
      } else {
        authUtils.removeUser();
        setIsAuthenticated(false);
      }
    } catch (err: any) {
      console.error('登录状态检查失败:', err);
      setIsAuthenticated(false);
    }
    setAuthChecking(false);
  };

  /**
   * 外部导入：下载论文并打开阅读器
   */
  const executeImport = async (intent: ImportIntent) => {
    setImportStatus('importing');
    setImportError('');

    try {
      const result = await apiPost('/api/import-paper', {
        pdf_url: intent.pdf_url,
        title: intent.title,
      }) as any;

      if (!result.success) {
        throw new Error(result.error || '导入失败');
      }

      const { fingerprint, file_name, total_pages, file_size, existed } = result;

      // 从服务器获取 PDF 二进制并缓存到 IndexedDB
      const pdfResp = await fetch(`/api/pdfs/${fingerprint}`, {
        credentials: 'include',
      });

      if (pdfResp.ok) {
        const pdfBlob = await pdfResp.blob();
        const pdfFileObj = new File([pdfBlob], file_name, {
          type: 'application/pdf',
          lastModified: Date.now(),
        });
        await savePDFFile(fingerprint, pdfFileObj);
      }

      // 直接打开阅读器
      const blob = await loadPDFFile(fingerprint);
      if (blob) {
        const file = new File([blob], file_name, { type: 'application/pdf' });
        const pdfUrl = URL.createObjectURL(blob);

        setOriginalFile(file);
        setPdfFile(pdfUrl);
        setPdfInfo({ fingerprint, total_pages, file_name, file_size });
        setCurrentPage(1);
        setSessionMode(true);
        setShowSessionManager(true);
        setCurrentView('viewer');
      }

      setImportStatus('done');
      setImportIntent(null);

      // 清除 URL 参数
      window.history.replaceState({}, '', window.location.pathname);

    } catch (err: any) {
      console.error('导入论文失败:', err);
      setImportStatus('error');
      setImportError(err.message || '导入失败');
    }
  };

  // 登录后自动触发导入
  useEffect(() => {
    if (isAuthenticated && importIntent && importStatus !== 'importing') {
      executeImport(importIntent);
    }
  }, [isAuthenticated, importIntent]);

  /**
   * 登录成功回调
   */
  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    // 如果有导入意图，不跳转到 library（useEffect 会自动触发 import）
    if (!importIntent) {
      setCurrentView('library');
    }
  };

  /**
   * PDF上传处理
   */
  const handleFileUpload = async (file: File, skipSave: boolean = false) => {
    setLoading(true);

    try {
      const pdfUrl = URL.createObjectURL(file);
      setOriginalFile(file);

      // 生成指纹
      const fingerprint = await generatePDFFingerprint(file);

      // 保存到IndexedDB（非skipSave时）
      if (!skipSave) {
        await savePDFFile(fingerprint, file);
      }

      // 上传到服务器（需要认证），带上fingerprint以便服务器保存PDF文件
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fingerprint', fingerprint);

      try {
        const response = await apiUpload('/api/upload', formData) as any;

        if (response.status === 'success') {
          setPdfInfo(response.pdf_info);
        }
      } catch (error: any) {
        console.error('后端上传失败:', error);
        alert('上传失败: ' + error.message);
        setLoading(false);
        return;
      }

      setPdfFile(pdfUrl);
      setCurrentPage(1);
      setCurrentView('viewer');

    } catch (error: any) {
      console.error('文件处理失败:', error);
      alert('文件处理失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 选择PDF（从文档库）
   */
  const handlePDFSelect = async (pdf: any, mode: string = 'independent') => {
    logger.log('选择PDF:', pdf, '模式:', mode);
    setLoading(true);

    try {
      // 从IndexedDB加载PDF文件
      const blob = await loadPDFFile(pdf.fingerprint);

      if (blob) {
        // 从blob创建File对象
        const file = new File([blob], pdf.file_name, { type: 'application/pdf' });
        const pdfUrl = URL.createObjectURL(blob);

        setOriginalFile(file);
        setPdfFile(pdfUrl);
        setPdfInfo({
          fingerprint: pdf.fingerprint,
          total_pages: pdf.total_pages,
          file_name: pdf.file_name,
          file_size: pdf.file_size
        });
        setCurrentPage(1);

        // 根据选择的模式设置sessionMode
        if (mode === 'session') {
          setSessionMode(true);
          setShowSessionManager(true); // 进入会话模式时显示会话管理器
          setCurrentView('viewer');
        } else if (mode === 'workspace') {
          setSessionMode(true);
          setShowSessionManager(true);
          setCurrentView('workspace');
        } else {
          // 独立模式
          setSessionMode(false);
          setCurrentSession(null);
          setCurrentView('viewer');
        }
      } else {
        // IndexedDB中没有文件，尝试从服务器下载
        logger.log('IndexedDB中未找到文件，尝试从服务器下载...');
        try {
          const response = await fetch(`/api/pdf/${pdf.fingerprint}/download`, {
            credentials: 'include',
          });

          if (response.ok) {
            const serverBlob = await response.blob();
            const file = new File([serverBlob], pdf.file_name, { type: 'application/pdf' });
            const pdfUrl = URL.createObjectURL(serverBlob);

            // 保存到IndexedDB以便下次使用
            await savePDFFile(pdf.fingerprint, serverBlob as any);

            setOriginalFile(file);
            setPdfFile(pdfUrl);
            setPdfInfo({
              fingerprint: pdf.fingerprint,
              total_pages: pdf.total_pages,
              file_name: pdf.file_name,
              file_size: pdf.file_size
            });
            setCurrentPage(1);

            // 根据选择的模式设置sessionMode
            if (mode === 'session') {
              setSessionMode(true);
              setShowSessionManager(true);
              setCurrentView('viewer');
            } else if (mode === 'workspace') {
              setSessionMode(true);
              setShowSessionManager(true);
              setCurrentView('workspace');
            } else {
              setSessionMode(false);
              setCurrentSession(null);
              setCurrentView('viewer');
            }
          } else {
            // 服务器也没有文件，提示重新上传
            if (window.confirm('本地缓存和服务器中都未找到此PDF文件，是否重新上传？')) {
              setCurrentView('upload');
            }
          }
        } catch (downloadError: any) {
          console.error('从服务器下载PDF失败:', downloadError);
          if (window.confirm('无法恢复PDF文件，是否重新上传？')) {
            setCurrentView('upload');
          }
        }
      }
    } catch (error: any) {
      console.error('加载PDF失败:', error);
      alert('加载PDF失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 点击上传按钮
   */
  const handleUploadClick = () => {
    setCurrentView('upload');
  };

  /**
   * 返回文档库
   */
  const handleBackToLibrary = () => {
    if (pdfFile) {
      URL.revokeObjectURL(pdfFile);
    }
    setPdfFile(null);
    setOriginalFile(null);
    setPdfInfo(null);
    setCurrentPage(1);
    setCurrentView('library');
  };

  /**
   * 页面切换处理
   */
  const handlePageChange = async (pageNumber: number) => {
    logger.log('App层页面切换:', currentPage, '->', pageNumber);

    // 确保页码在有效范围内
    if (pageNumber < 1) pageNumber = 1;
    if (pdfInfo && pageNumber > pdfInfo.total_pages) {
      pageNumber = pdfInfo.total_pages;
    }

    if (pageNumber !== currentPage) {
      setCurrentPage(pageNumber);
      logger.log('页码状态已更新:', pageNumber);

      // 如果在会话模式下，更新会话的last_page
      if (sessionMode && currentSession) {
        try {
          await apiPatch(`/api/reading-sessions/${currentSession.id}`, {
            last_page: pageNumber
          });
          logger.log('会话页码已更新:', pageNumber);

          // 更新本地会话状态
          setCurrentSession(prev => prev ? ({
            ...prev,
            last_page: pageNumber
          }) : null);
        } catch (error: any) {
          console.error('更新会话页码失败:', error);
        }
      }
    }
  };

  /**
   * 选择会话
   */
  const handleSessionSelect = (session: Session) => {
    setCurrentSession(session);
    logger.log('选择会话:', session);

    // 如果会话有保存的页码，跳转到该页
    if (session && session.last_page && session.last_page > 0) {
      setCurrentPage(session.last_page);
      logger.log('跳转到会话保存的页码:', session.last_page);
    }

    // 如果是从 workspace 模式触发的会话选择，保持在 workspace 视图
    // 逻辑：当前视图已经在 render 中根据 currentView 决定
  };

  /**
   * 打开会话管理器
   */
  const handleManageSessions = () => {
    setShowSessionManager(true);
  };

  // 加载中状态
  if (authChecking) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>加载中...</p>
      </div>
    );
  }

  // 功能介绍页 (无需登录即可访问)
  if (currentView === 'features') {
    return (
      <FeatureIntro
        onStart={() => setCurrentView('library')}
        onBack={() => setCurrentView('library')}
      />
    );
  }

  // 未登录，显示登录页
  if (!isAuthenticated) {
    return (
      <LoginPage
        onLoginSuccess={handleLoginSuccess}
        onShowFeatures={() => setCurrentView('features')}
        importHint={importIntent ? importIntent.title : null}
      />
    );
  }

  // 正在导入论文
  if (importStatus === 'importing') {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>正在导入论文...</p>
        <p style={{ fontSize: '14px', color: '#888', marginTop: '8px' }}>{importIntent?.title}</p>
      </div>
    );
  }

  // 导入失败
  if (importStatus === 'error') {
    return (
      <div className="app-loading">
        <p style={{ color: '#e53e3e', fontSize: '18px' }}>导入失败</p>
        <p style={{ color: '#888', marginTop: '8px' }}>{importError}</p>
        <button
          onClick={() => { setImportStatus(null); setImportIntent(null); setCurrentView('library'); window.history.replaceState({}, '', window.location.pathname); }}
          style={{ marginTop: '16px', padding: '8px 24px', borderRadius: '6px', border: '1px solid #ddd', cursor: 'pointer' }}
        >
          返回文档库
        </button>
      </div>
    );
  }

  // Canvas-only 模式（独立窗口）
  if (currentView === 'canvas-only' && currentSession) {
    return (
      <div className="app canvas-only-app">
        <div className="canvas-only-header">
          <h1>📐 Canvas 空间视图</h1>
          <span className="session-id-badge">
            会话 ID: {currentSession.id.substring(0, 8)}...
          </span>
        </div>
        <div className="canvas-only-content">
          <SessionCanvas
            sessionId={currentSession.id}
            pdfFingerprint={currentSession.pdf_fingerprint || pdfInfo?.fingerprint}
            readOnly={false}
          />
        </div>
      </div>
    );
  }

  // 已登录，根据currentView显示不同页面
  return (
    <div className="app">
      {currentView === 'library' && (
        <PDFLibrary
          onPDFSelect={handlePDFSelect}
          onUploadClick={handleUploadClick}
          onShowFeatures={() => setCurrentView('features')}
        />
      )}

      {currentView === 'upload' && (
        <div>
          <header className="app-header">
            <button onClick={handleBackToLibrary} className="back-btn">
              ← 返回文档库
            </button>
            <h1>上传PDF文档</h1>
          </header>

          <FileUpload
            onUpload={handleFileUpload}
            loading={loading}
            pdfHistory={[]} // 不显示历史记录（在文档库中显示）
            onUpdateHistory={() => {}}
          />
        </div>
      )}

      {currentView === 'viewer' && pdfFile && (
        <div>
          {/* 紧凑的单行工具栏 */}
          <header className="compact-toolbar">
            <div className="toolbar-left">
              <button onClick={handleBackToLibrary} className="back-btn-compact" title="返回文档库">
                ←
              </button>

              {/* 会话模式信息 */}
              {sessionMode && (
                <>
                  <span className="mode-badge-compact session-mode">📚 会话模式</span>
                  <button className="switch-session-btn-compact" onClick={handleManageSessions}>
                    切换会话
                  </button>
                </>
              )}

              {/* 独立模式 */}
              {!sessionMode && (
                <span className="mode-badge-compact independent-mode">🎯 独立模式</span>
              )}
            </div>

            <div className="toolbar-right">
              {/* 视图切换（仅会话模式） */}
              {sessionMode && currentSession && (
                <>
                  <button
                    className="workspace-btn-compact"
                    onClick={() => setCurrentView('workspace')}
                    title="进入工作台"
                  >
                    🖥️ 进入工作台 (Split View)
                  </button>
                  <ViewModeToggle
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                  />
                </>
              )}
            </div>
          </header>

          <div className="content-container">
            {/* PDF 视图 */}
            {viewMode === 'pdf' && (
              <PDFViewerWithAI
                pdfFile={pdfFile}
                originalFile={originalFile}
                onPageChange={handlePageChange}
                currentPage={currentPage}
                sessionMode={sessionMode}
                currentSession={currentSession as any}
                existingFingerprint={pdfInfo?.fingerprint as any}
              />
            )}

            {/* Canvas 视图（仅在会话模式下可用）*/}
            {viewMode === 'canvas' && sessionMode && currentSession && (
              <SessionCanvas
                sessionId={currentSession.id}
                pdfFingerprint={currentSession.pdf_fingerprint || pdfInfo?.fingerprint}
                readOnly={false}
              />
            )}
          </div>

          {/* 会话管理器模态框 */}
          {showSessionManager && pdfInfo && (
            <SessionManager
              pdfFingerprint={pdfInfo.fingerprint}
              onSessionSelect={handleSessionSelect}
              onClose={() => setShowSessionManager(false)}
            />
          )}
        </div>
      )}

      {/* Unified Workspace 视图 */}
      {currentView === 'workspace' && pdfFile && currentSession && (
        <div className="workspace-wrapper">
          <header className="app-header-with-session" style={{ marginBottom: 0 }}>
             <div className="app-header">
              <button onClick={() => setCurrentView('viewer')} className="back-btn">
                ← 返回阅读器
              </button>
              <h1>{currentSession.name} - 知识工作台</h1>
              <div className="header-controls">
                <span className="session-badge">WorkSpace Mode</span>
              </div>
            </div>
          </header>
          <UnifiedWorkspace
            pdfFile={pdfFile}
            originalFile={originalFile}
            currentSession={currentSession}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onBack={() => setCurrentView('viewer')}
            existingFingerprint={pdfInfo?.fingerprint as any}
          />
        </div>
      )}

      {/* 会话管理器（Workspace 模式下也可能需要，或者暂时禁用） */}
      {currentView === 'workspace' && showSessionManager && (
         <SessionManager
          pdfFingerprint={pdfInfo?.fingerprint as any}
          onSessionSelect={handleSessionSelect}
          onClose={() => setShowSessionManager(false)}
        />
      )}
    </div>
  );
}

export default App;
